import { readZipEntries } from './zip-entries';

/**
 * Google Takeout → review import rows (#173).
 *
 * Takeout's "Google Business Profile" product exports one folder per listing:
 *
 *   Takeout/Google Business Profile/account-…/location-…/reviews.json
 *   Takeout/Google Business Profile/account-…/location-…/additionalData.json
 *
 * reviews.json is `{ reviews: Review[] }` in the mybusiness/v4 Review shape —
 * the same shape the API returns — so the server's import is one code path for
 * both sources. additionalData.json carries the listing name (LISTING_NAME
 * attribute), which is not on the review itself.
 */

export interface GoogleReviewImportRow {
  googleReviewId: string;
  locationName: string | null;
  reviewerName: string | null;
  starRating: number | null;
  comment: string | null;
  replyComment: string | null;
  createTime: string | null;
  updateTime: string | null;
}

export interface ParsedTakeout {
  reviews: GoogleReviewImportRow[];
  /** Locations this export is a complete copy of — removals are scoped to these. */
  locationIds: string[];
  /** Listing folders found, including ones with no reviews. */
  folders: number;
}

const STARS: Record<string, number> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
const REVIEW_NAME = /^(accounts\/[^/]+\/locations\/[^/]+)\/reviews\/[^/]+$/;
const REVIEWS_FILE = /(^|\/)reviews[^/]*\.json$/i;
const LISTING_FILE = /(^|\/)additionalData\.json$/i;

function folderOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i >= 0 ? path.slice(0, i) : '';
}

/** The LISTING_NAME attribute from additionalData.json, or null. */
export function listingNameFrom(data: any): string | null {
  const attrs = Array.isArray(data?.attributes) ? data.attributes : [];
  const listing = attrs.find((a: any) => a?.id?.attributeType === 'LISTING_NAME');
  const text = listing?.values?.[0]?.datum?.listingName?.text;
  return typeof text === 'string' && text.trim() ? text.trim() : null;
}

/** One v4 Review → an import row; null when it has no usable review id. */
export function reviewRowFrom(review: any, locationName: string | null): GoogleReviewImportRow | null {
  const name = typeof review?.name === 'string' ? review.name : '';
  if (!REVIEW_NAME.test(name)) return null;
  const str = (v: any) => (typeof v === 'string' && v.trim() ? v : null);
  return {
    googleReviewId: name,
    locationName,
    reviewerName: str(review?.reviewer?.displayName),
    starRating: STARS[review?.starRating] ?? null,
    comment: str(review?.comment),
    replyComment: str(review?.reviewReply?.comment),
    createTime: str(review?.createTime),
    updateTime: str(review?.updateTime),
  };
}

/**
 * Parse a set of { path → JSON } files from a Takeout export. Paths group the
 * reviews with their listing name; a bare reviews.json (no folder) still works,
 * it just has no listing name.
 */
export function parseTakeoutFiles(files: { path: string; json: any }[]): ParsedTakeout {
  const names = new Map<string, string | null>();
  for (const f of files) {
    if (LISTING_FILE.test(f.path)) names.set(folderOf(f.path), listingNameFrom(f.json));
  }

  const byId = new Map<string, GoogleReviewImportRow>();
  const locationIds = new Set<string>();
  const folders = new Set<string>(names.keys());
  for (const f of files) {
    if (!REVIEWS_FILE.test(f.path)) continue;
    const folder = folderOf(f.path);
    folders.add(folder);
    const list = Array.isArray(f.json?.reviews) ? f.json.reviews : [];
    for (const review of list) {
      const row = reviewRowFrom(review, names.get(folder) ?? null);
      if (!row) continue;
      byId.set(row.googleReviewId, row);
      locationIds.add(REVIEW_NAME.exec(row.googleReviewId)![1]);
    }
  }

  return { reviews: Array.from(byId.values()), locationIds: Array.from(locationIds), folders: folders.size };
}

/**
 * Read Takeout .zip part(s), or loose .json files, from a file input. Select
 * every part together: Google splits a listing's folder across parts (the
 * first real export had every reviews.json in part 1 and every
 * additionalData.json in part 2), and names are joined by folder across all.
 */
export async function parseTakeoutUpload(fileList: File[]): Promise<ParsedTakeout> {
  const files: { path: string; json: any }[] = [];
  for (const file of fileList) {
    if (/\.zip$/i.test(file.name)) {
      // Never load the archive whole: Takeout parts are ~2 GB of photos with a
      // few MB of JSON, past the browser's single-buffer limit (zip-entries.ts).
      const entries = await readZipEntries(file, (name) => REVIEWS_FILE.test(name) || LISTING_FILE.test(name));
      for (const entry of entries) {
        files.push({ path: entry.name, json: JSON.parse(entry.text) });
      }
    } else if (/\.json$/i.test(file.name)) {
      // webkitRelativePath keeps the folder when a whole directory is picked.
      const path = (file as any).webkitRelativePath || file.name;
      files.push({ path, json: JSON.parse(await file.text()) });
    }
  }
  return parseTakeoutFiles(files);
}
